import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { 
  Button, 
  Input, 
  Logo, 
  DualPaneLayout, 
  colors, 
  spacing, 
  typography 
} from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { handleApiError, logError } from '../utils/errorHandler';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface InvitationAcceptanceScreenProps {
  route?: {
    params?: {
      token?: string;
    };
  };
  // For navigation from business setup screen
  navigation?: {
    getParam?: (key: string) => string;
  };
}

export const InvitationAcceptanceScreen: React.FC<InvitationAcceptanceScreenProps> = ({ route }) => {
  const styles = useThemedStyles(getStyles);
  const { 
    showNotification,
    navigateTo,
  } = useAppContext();

  const { setUser, setTokens } = useAppStore();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [token, setToken] = useState(route?.params?.token || '');
  const [invitationData, setInvitationData] = useState<any>(null);
  const [error, setError] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Auto-bind token from URL and start verification
  useEffect(() => {
    try {
      const routeToken = route?.params?.token;
      const globalToken = (global as any).pendingInvitationToken;
      
      // Extract token from URL query parameters (for web URLs like /accept-invitation?token=...)
      const urlParams = new URLSearchParams(window.location?.search || '');
      const urlToken = urlParams.get('token');
      
      if (urlToken) {
        console.log('🔍 [InvitationAcceptanceScreen] Token found in URL:', urlToken);
        setToken(urlToken);
        // Auto-start verification when token is in URL
        setTimeout(() => {
          handleAcceptInvitation(urlToken);
        }, 500); // Small delay to ensure UI is ready
      } else if (routeToken) {
        console.log('🔍 [InvitationAcceptanceScreen] Token found in route params:', routeToken);
        setToken(routeToken);
        // Auto-start verification when token is in URL
        setTimeout(() => {
          handleAcceptInvitation(routeToken);
        }, 500); // Small delay to ensure UI is ready
      } else if (globalToken) {
        console.log('🔍 [InvitationAcceptanceScreen] Token found in global state:', globalToken);
        setToken(globalToken);
        // Clear the global token after using it
        (global as any).pendingInvitationToken = undefined;
      }
    } catch (error) {
      console.error('❌ [InvitationAcceptanceScreen] Error in auto-verification:', error);
      setError('Failed to process invitation token');
      setHasError(true);
    }
  }, [route?.params?.token]);

  const handleAcceptInvitation = async (invitationToken?: string) => {
    const tokenToUse = invitationToken || token;
    
    if (!tokenToUse) {
      setError('Please enter a valid invitation token');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      console.log('🔍 [InvitationAcceptanceScreen] Calling acceptInvitation with token:', tokenToUse);
      const result = await apiService.acceptInvitation(tokenToUse);
      console.log('🔍 [InvitationAcceptanceScreen] API response:', result);

      if (result.statusCode === 1000) {
        console.log('🔍 [InvitationAcceptanceScreen] Response data:', result.data);
        console.log('🔍 [InvitationAcceptanceScreen] user:', result.data.user);
        console.log('🔍 [InvitationAcceptanceScreen] business:', result.data.business);

        const invitedEmail = result.data?.user?.email;
        if (!invitedEmail) {
          setError('This invitation is missing an email address to verify.');
          setHasError(true);
          return;
        }

        // Unified auth: verify the invitee owns the invited email via OTP instead of asking
        // them to set a password. Store the invitation token + target business so
        // OTPVerificationScreen can activate the membership and land directly on that
        // business (not the generic memberships picker) once the code is verified.
        (global as any).invitationToken = tokenToUse;
        (global as any).invitationBusinessId = result.data?.business?._id;

        const otpResponse = await apiService.sendEmailLoginOTP(invitedEmail);
        if (otpResponse.statusCode !== 1000) {
          const errorMessage = otpResponse.errors?.[0]?.message || 'Failed to send verification code';
          setError(errorMessage);
          setHasError(true);
          showNotification('error', 'Invitation Error', errorMessage);
          return;
        }

        navigateTo('otp-verification', undefined, {
          verificationType: 'invitation-email',
          email: invitedEmail,
        });
      } else {
        console.log('🔍 [InvitationAcceptanceScreen] API error:', result.errors);
        const errorMessage = result.errors?.[0]?.message || 'Failed to accept invitation';
        console.log('🔍 [InvitationAcceptanceScreen] Setting error message:', errorMessage, 'type:', typeof errorMessage);
        setError(errorMessage);
        setHasError(true);
        showNotification('error', 'Invitation Error', errorMessage);
      }
    } catch (error: any) {
      console.error('❌ [InvitationAcceptanceScreen] Error accepting invitation:', error);
      const errorInfo = handleApiError(error);
      const errorMessage = typeof errorInfo === 'string' ? errorInfo : errorInfo.message;
      console.log('🔍 [InvitationAcceptanceScreen] Setting error message from catch:', errorMessage, 'type:', typeof errorMessage);
      setError(errorMessage);
      setHasError(true);
      showNotification('error', 'Invitation Error', errorMessage);
      logError(error, 'InvitationAcceptanceScreen');
    } finally {
      setIsVerifying(false);
    }
  };


  if (isVerifying) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Verifying invitation...</Text>
        </View>
      </SafeAreaView>
    );
  }

  // Show error state if there's an error
  if ((error || hasError) && !isVerifying) {
    console.log('🔍 [InvitationAcceptanceScreen] Rendering error state:', { error, hasError, errorType: typeof error });
    
    // Fallback error display in case DualPaneLayout fails
    try {
      return (
        <DualPaneLayout title="DISLIX" subtitle="Invitation Error">
          <SafeAreaView style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
            
            <KeyboardAvoidingView 
              style={styles.keyboardAvoidingView}
              behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
              keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
            >
              <ScrollView 
                contentContainerStyle={styles.content}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                {/* Header */}
                <View style={styles.header}>
                  <Logo size="medium" variant="white" />
                </View>

                {/* Error Content */}
                <View style={styles.errorContent}>
                  <View style={styles.errorIcon}>
                    <Text style={styles.errorIconText}>⚠</Text>
                  </View>
                  <Text style={styles.errorTitle}>Invitation Error</Text>
                  <Text style={styles.errorMessage}>{typeof error === 'string' ? error : JSON.stringify(error)}</Text>
                  
                  <View style={styles.errorActions}>
                    <View style={styles.buttonContainer}>
                      <Button
                        title="Sign Up"
                        onPress={() => navigateTo('register')}
                        variant="primary"
                        size="lg"
                        style={styles.primaryButton}
                      />
                      
                      <Button
                        title="Login"
                        onPress={() => navigateTo('unified-login')}
                        variant="outline"
                        size="lg"
                        style={styles.secondaryButton}
                      />
                    </View>
                    
                    <View style={styles.helpContainer}>
                      <Text style={styles.helpIcon}>💡</Text>
                      <Text style={styles.helpText}>
                        Ask your business management to invite you again
                      </Text>
                    </View>
                  </View>
                </View>
              </ScrollView>
            </KeyboardAvoidingView>
          </SafeAreaView>
        </DualPaneLayout>
      );
    } catch (layoutError) {
      console.error('❌ [InvitationAcceptanceScreen] Error rendering error layout:', layoutError);
      // Fallback simple error display
      return (
        <DualPaneLayout title="DISLIX" subtitle="Invitation Error">
          <SafeAreaView style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
            
            <KeyboardAvoidingView 
              style={styles.keyboardAvoidingView}
              behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
              keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
            >
              <ScrollView 
                contentContainerStyle={styles.content}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                {/* Header */}
                <View style={styles.header}>
                  <Logo size="medium" variant="white" />
                </View>

                {/* Error Content */}
                <View style={styles.errorContent}>
                  <Text style={styles.errorTitle}>Invitation Error</Text>
                  <Text style={styles.errorMessage}>Invalid or expired invitation token</Text>
                  
                  <View style={styles.errorActions}>
                    <View style={styles.buttonContainer}>
                      <Button
                        title="Sign Up"
                        onPress={() => navigateTo('register')}
                        variant="primary"
                        size="lg"
                        style={styles.primaryButton}
                      />
                      
                      <Button
                        title="Login"
                        onPress={() => navigateTo('unified-login')}
                        variant="outline"
                        size="lg"
                        style={styles.secondaryButton}
                      />
                    </View>
                    
                    <View style={styles.helpContainer}>
                      <Text style={styles.helpIcon}>💡</Text>
                      <Text style={styles.helpText}>
                        Ask your business management to invite you again
                      </Text>
                    </View>
                  </View>
                </View>
              </ScrollView>
            </KeyboardAvoidingView>
          </SafeAreaView>
        </DualPaneLayout>
      );
    }
  }

  if (invitationData) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        <DualPaneLayout
          title="Invitation Accepted"
          subtitle="Welcome to the team!"
        >
          <View style={styles.successContainer}>
            <View style={styles.successIcon}>
              <Text style={styles.successIconText}>✓</Text>
            </View>
            <Text style={styles.successTitle}>Invitation Accepted!</Text>
            <Text style={styles.successMessage}>
              You have successfully joined the team. You can now log in to access your account.
            </Text>
            <Button
              title="Continue to Login"
              onPress={() => navigateTo('unified-login')}
              style={styles.continueButton}
            />
          </View>
        </DualPaneLayout>
      </SafeAreaView>
    );
  }

  return (
    <DualPaneLayout
      title="DISLIX"
      subtitle="Accept Invitation"
    >
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        
        <KeyboardAvoidingView 
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          <ScrollView 
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Header */}
            <View style={styles.header}>
              <Logo size="medium" variant="white" />
            </View>

            {/* Form */}
            <View style={styles.form}>
              <Input
                label="Invitation Token"
                placeholder="Enter your invitation token"
                value={token}
                onChangeText={setToken}
                size="md"
                autoCapitalize="none"
                autoCorrect={false}
                error={error}
                editable={!isLoading && !needsPassword}
              />

              {/* Accept Button */}
              <Button
                title={isLoading ? "Processing..." : "Accept Invitation"}
                onPress={() => handleAcceptInvitation()}
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
                disabled={!token.trim() || isLoading}
                style={styles.acceptButton}
              />

              {/* Help Text */}
              <View style={styles.resendContainer}>
                <Text style={[
                  styles.resendText,
                  isLoading && styles.disabledText
                ]}>
                  Ask your business management to invite you again
                </Text>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  errorContent: {
    paddingHorizontal: spacing.screenPadding,
    alignItems: 'center',
  },
  form: {
    paddingHorizontal: spacing.screenPadding,
  },
  acceptButton: {
    marginTop: spacing.lg,
    marginBottom: spacing.xl,
  },
  resendContainer: {
    alignItems: 'center',
  },
  resendText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  disabledText: {
    opacity: 0.5,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.primary,
    color: colors.text.secondary,
    marginTop: spacing.md,
  },
  successContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.screenPadding,
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.success,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  successIconText: {
    fontSize: 40,
    color: colors.white,
    fontFamily: typography.fontFamily.bold,
  },
  successTitle: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  successMessage: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.primary,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.xl * 2,
  },
  continueButton: {
    width: '100%',
    maxWidth: 300,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.screenPadding,
  },
  errorIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.error,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  errorIconText: {
    fontSize: 40,
    color: colors.white,
    fontFamily: typography.fontFamily.bold,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  errorMessage: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.primary,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.lg,
  },
  errorActions: {
    width: '100%',
    maxWidth: 400,
    marginTop: spacing.lg,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
    gap: spacing.lg,
  },
  primaryButton: {
    flex: 1,
    minHeight: 60,
    marginRight: spacing.sm,
    borderRadius: 16,
    shadowColor: colors.primary,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  secondaryButton: {
    flex: 1,
    minHeight: 60,
    marginLeft: spacing.sm,
    borderRadius: 16,
  },
  helpContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
  },
  helpIcon: {
    fontSize: 16,
    marginRight: spacing.sm,
  },
  helpText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 20,
    flex: 1,
  },
});

export default InvitationAcceptanceScreen;
